import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import {
  SuggestService,
  SuggestResponse,
  SuggestCompany,
  SuggestTag,
} from '@/lib/api/services/suggest'

interface SuggestState {
  suggestData: SuggestResponse | null
  loading: boolean
  error: string | null
}

const initialState: SuggestState = {
  suggestData: null,
  loading: false,
  error: null,
}

// サジェストデータ取得用の非同期thunk
export const fetchSuggestData = createAsyncThunk(
  'suggest/fetchSuggestData',
  async (_, { getState, rejectWithValue }) => {
    try {
      // ストアにデータが既に存在するかチェック
      const state = getState() as { suggest: SuggestState }
      if (state.suggest.suggestData) {
        return state.suggest.suggestData
      }

      const data = await SuggestService.getSuggestData()
      return data
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch suggest data',
      )
    }
  },
)

const suggestSlice = createSlice({
  name: 'suggest',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSuggestData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSuggestData.fulfilled, (state, action: PayloadAction<SuggestResponse>) => {
        state.loading = false
        state.suggestData = action.payload
        state.error = null
      })
      .addCase(fetchSuggestData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

// セレクター
export const selectSuggestData = (state: { suggest: SuggestState }) => state.suggest.suggestData
export const selectSuggestLoading = (state: { suggest: SuggestState }) => state.suggest.loading
export const selectSuggestError = (state: { suggest: SuggestState }) => state.suggest.error

// フィルターされたサジェストセレクター - Nuxt版と同様
export const selectFilteredSuggestions =
  (state: { suggest: SuggestState }) => (searchWord: string) => {
    if (!state.suggest.suggestData) return { companies: [], categories: [] }

    const searchWords = searchWord.toLowerCase().split(' ').filter(Boolean)

    // まず企業名でフィルター
    const nameFilteredCompanies = state.suggest.suggestData.companies
      .filter((company: SuggestCompany) =>
        searchWords.every((word) => company.name.toLowerCase().includes(word)),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 3)

    // 企業名でと3社未満の場合、openUrl、enName、kanaで検索
    let additionalCompanies: SuggestCompany[] = []
    if (nameFilteredCompanies.length < 3) {
      additionalCompanies = state.suggest.suggestData.companies
        .filter((company: SuggestCompany) => {
          return searchWords.every(
            (word) =>
              !nameFilteredCompanies.includes(company) && // すでに見つかった企業を除外
              (company.openUrl.toLowerCase().includes(word) ||
                company.enName?.toLowerCase().includes(word) ||
                company.kana?.toLowerCase().includes(word)),
          )
        })
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 3 - nameFilteredCompanies.length) // 残りのスロットを埋める
    }

    const companies = [...nameFilteredCompanies, ...additionalCompanies]

    // カテゴリーをフィルター
    const categories = state.suggest.suggestData.tags
      .filter((tag: SuggestTag) =>
        searchWords.every((word) => tag.name.toLowerCase().includes(word)),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 3)

    return { companies, categories }
  }

export const { clearError } = suggestSlice.actions
export default suggestSlice.reducer
